import React, { useCallback, useEffect, useState } from 'react'
import { DateTime, Interval } from 'luxon'
import Sticky from 'react-sticky-fill'
import Repeatable from 'react-repeatable'
import Events from './Events'
import { ReactComponent as Arrow } from './images/arrow_schedule.svg'

function* daysMap(interval) {
  let i = 0
  let cursor = interval.start.startOf('day')

  while (cursor < interval.end) {
    yield [i, cursor]
    cursor = cursor.plus({ days: 1 })
    i++
  }
}

const HourlyView = (props) => {
  const {
    events,
    filters,
    ordering,
    screenings,
    ticketing,
    title,
    path,
    trackList,
    topFestival,
  } = props

  const [days, setDays] = useState([])
  const [activeDay, setActiveDay] = useState(DateTime.local())
  const [cellSize, setCellSize] = useState(window.innerWidth < 960 ? 194 : 270)

  const changeCellSize = useCallback(() => {
    if (window.innerWidth < 960) {
      setCellSize(194)
    } else {
      setCellSize(270)
    }
  })

  let grid = document.querySelector('.schedule__grid')
  let hours = document.querySelector('.schedule__hours__inner')
  let connectScrolling = null

  useEffect(() => {
    window.addEventListener('load', changeCellSize)
    window.addEventListener('resize', changeCellSize)

    return () => {
      window.onload = changeCellSize
      window.removeEventListener('resize', changeCellSize)
    }
  }, [])

  useEffect(() => {
    if (events.length) {
      const editedDays = [...days]

      let minDate = DateTime.fromISO(events[0].start)

      events.forEach((event) => {
        event.start = DateTime.fromISO(event.start)
        event.end = DateTime.fromISO(event.end)

        if (event.start < minDate) {
          minDate = event.start
        }

        const startString = `${event.start.get('monthShort')} ${event.start.get(
          'day',
        )}`
        const endString = `${event.end.get('monthShort')} ${event.end.get(
          'day',
        )}`

        if (startString !== endString) {
          let interval = Interval.fromDateTimes(event.start, event.end)

          const daysCount = interval.count('days')

          for (const [index, day] of daysMap(interval)) {
            let startDay = `${day.get('monthShort')} ${day.get('day')}`
            const foundDay = editedDays.find((d) => d.day === startDay)

            const repeatingEvent = { ...event }

            if (index === 0) {
              repeatingEvent.end = day.endOf('day')
            } else if (index === daysCount - 1) {
              repeatingEvent.start = day
            } else {
              repeatingEvent.start = day
              repeatingEvent.end = day.endOf('day')
            }

            if (foundDay) {
              foundDay.events.push(repeatingEvent)
            } else {
              editedDays.push({
                day: startDay,
                date: repeatingEvent.start,
                events: [repeatingEvent],
              })
            }
          }
        } else {
          const startDay = `${event.start.get('monthShort')} ${event.start.get(
            'day',
          )}`
          const foundDay = editedDays.find((d) => d.day === startDay)

          if (foundDay) {
            foundDay.events.push(event)
          } else {
            editedDays.push({
              day: startDay,
              date: event.start,
              events: [event],
            })
          }
        }
      })

      // sets up hours and event positions
      editedDays.forEach((day) => {
        const startTime = day.date.startOf('day')

        day.min = startTime
        day.events.forEach((event) => {
          event.startPosition = (event.start.hour - startTime.hour) * 4
          // move events over if they aren't exactly on the hour
          if (event.start.minute) {
            if (event.start.minute <= 15) {
              event.startPosition++
            } else if (event.start.minute <= 30) {
              event.startPosition += 2
            } else if (event.startPosition <= 45) {
              event.startPosition += 3
            } else {
              event.startPosition += 4
            }
          }
          event.endPosition = parseInt(
            event.startPosition +
              event.end.diff(event.start, 'hours').hours * 4,
            10,
          )
        })
      })

      setActiveDay(minDate)
      setDays(editedDays)
    }
  }, [events])

  useEffect(() => {
    grid = document.querySelector('.schedule__grid')
    hours = document.querySelector('.schedule__hours__inner')

    if (days.length) {
      connectScrolling = (event) => {
        const scrollLeft = event.target.scrollLeft

        if (event.target.classList.contains('schedule__hours__inner')) {
          grid.scrollLeft = scrollLeft
        } else {
          hours.scrollLeft = scrollLeft
        }
      }

      grid.addEventListener('scroll', connectScrolling)
      hours.addEventListener('scroll', connectScrolling)
    }

    return () => {
      if (grid) {
        grid.removeEventListener('scroll', connectScrolling)
        hours.removeEventListener('scroll', connectScrolling)
      }
    }
  }, [days, activeDay])

  useEffect(() => {
    hours.scrollLeft = activeDay.get('hour') * cellSize
    grid.scrollLeft = activeDay.get('hour') * cellSize
  }, [days, trackList])

  useEffect(() => {
    if (filters.date) {
      const newDate = DateTime.fromISO(filters.date)
      setActiveDay(newDate.startOf('day'))

      hours.scrollLeft = 0
      grid.scrollLeft = 0
    }
  }, [filters.date])

  const scrollHoursLeft = () => {
    if (grid.scrollLeft > 0) {
      hours.scrollBy({
        top: 0,
        left: -window.innerWidth,
        behavior: 'smooth',
      })

      grid.scrollBy({
        top: 0,
        left: -window.innerWidth,
        behavior: 'smooth',
      })
    } else {
      previousDay()
      grid.scrollLeft = grid.scrollWidth - window.innerWidth
      hours.scrollLeft = grid.scrollWidth - window.innerWidth
    }
  }

  const scrollHoursRight = () => {
    if (grid.scrollLeft === grid.scrollWidth - window.innerWidth) {
      nextDay()
      grid.scrollLeft = 0
      hours.scrollLeft = 0
    } else {
      hours.scrollBy({
        top: 0,
        left: window.innerWidth,
        behavior: 'smooth',
      })

      grid.scrollBy({
        top: 0,
        left: window.innerWidth,
        behavior: 'smooth',
      })
    }
  }

  const previousDay = () => {
    setActiveDay(activeDay.minus({ days: 1 }))
  }

  const nextDay = () => {
    setActiveDay(activeDay.plus({ days: 1 }))
  }

  const getEvents = (track) => {
    let startDay = `${activeDay.get('monthShort')} ${activeDay.get('day')}`
    const foundDay = days.find((d) => d.day === startDay)

    if (foundDay) {
      if (track === 'unassigned') {
        return foundDay.events.filter((e) => !e.tracks.length)
      } else {
        return foundDay.events.filter((e) => e.tracks.includes(track))
      }
    } else {
      return []
    }
  }

  return (
    <div className='schedule__wrapper'>
      <div className='schedule__header'>
        <h2 className='schedule__day-header'>
          {activeDay.toFormat('ccc. LLL. d, y')}
        </h2>

        <div className='schedule__hours'>
          <div className='schedule__hours__inner'>
            <Repeatable
              className={`prev ${
                topFestival ? topFestival.toLowerCase().replace(/\s/, '-') : ''
              }`}
              aria-label='previous'
              tag='button'
              repeatDelay={500}
              repeatInterval={500}
              onTouchStart={(event) => event.preventDefault}
              onClick={scrollHoursLeft}
              onHold={scrollHoursLeft}
            >
              <Arrow />
            </Repeatable>

            {[...Array(24)].map((_, index) => (
              <h3 key={index}>
                {activeDay
                  .startOf('day')
                  .plus({ hours: index })
                  .toLocaleString(DateTime.TIME_SIMPLE)
                  .toLowerCase()
                  .replace(' ', '')}
              </h3>
            ))}

            <Repeatable
              className={`next ${
                topFestival ? topFestival.toLowerCase().replace(/\s/, '-') : ''
              }`}
              aria-label='next'
              tag='button'
              repeatDelay={500}
              repeatInterval={500}
              onTouchStart={(event) => event.preventDefault}
              onClick={scrollHoursRight}
              onHold={scrollHoursRight}
            >
              <Arrow />
            </Repeatable>
          </div>
        </div>
      </div>

      <div className='schedule'>
        <div className='schedule__grid'>
          {days.find((d) => d.events.find((e) => !e.tracks.length)) &&
            filters.track === 'all' && (
              <div
                className='schedule__row'
                style={{
                  minWidth: cellSize * 24,
                  maxWidth: cellSize * 24,
                }}
              >
                <Sticky className='track'>
                  <div className='track__inner'>
                    <h3>{title}</h3>
                  </div>
                </Sticky>

                <div className='schedule__borders'>
                  {[...Array(24)].map((_, index) => (
                    <span key={index} />
                  ))}
                </div>

                <Events
                  rows={24}
                  events={getEvents('unassigned')}
                  cellSize={cellSize}
                  filters={filters}
                  screenings={screenings}
                  ticketing={ticketing}
                  path={path}
                />
              </div>
            )}

          {trackList
            .sort((a, b) => {
              const aOrder = ordering.find((o) => o.name === a)
              const bOrder = ordering.find((o) => o.name === b)

              if (aOrder && bOrder) {
                return aOrder.position > bOrder.position ? 1 : -1
              }

              return 2
            })
            .map((track) => (
              <React.Fragment key={track}>
                {days.find((d) =>
                  d.events.find((e) => e.tracks.includes(track)),
                ) &&
                  track !== 'unassigned' &&
                  (filters.track === 'all' || filters.track === track) && (
                    <div
                      className='schedule__row'
                      style={{
                        minWidth: cellSize * 24,
                        maxWidth: cellSize * 24,
                      }}
                    >
                      <Sticky className='track'>
                        <div className='track__inner'>
                          <h3>{track}</h3>
                        </div>
                      </Sticky>

                      <div className='schedule__borders'>
                        {[...Array(24)].map((_, index) => (
                          <span key={index} />
                        ))}
                      </div>

                      <Events
                        rows={24}
                        events={getEvents(track)}
                        cellSize={cellSize}
                        filters={filters}
                        screenings={screenings}
                        ticketing={ticketing}
                        path={path}
                      />
                    </div>
                  )}
              </React.Fragment>
            ))}
        </div>
      </div>
    </div>
  )
}

export default HourlyView
